import { useTranslation } from 'react-i18next';
import { useLanguageLabels } from '~/hooks/useLanguageLabels';
import type { LanguageCode } from '~/types/congregationGroupTypes';
import { formatLongDate } from '~/utils/dateFormat';
import WbsTable, { type Column } from './WbsTable';

export interface StudyHelperHistoryRecord {
  id: string;
  studyHelper: string;
  location: string;
  /** The helper's teaching languages, not their account language. */
  languages: LanguageCode[];
  adoptedAt: string;
  releasedAt: string | null;
  releasedBy: string | null;
}

interface StudyHelpersHistoryTableProps {
  studentName: string;
  data: StudyHelperHistoryRecord[];
}

export function StudyHelpersHistoryTable({
  studentName,
  data,
}: StudyHelpersHistoryTableProps) {
  const { t } = useTranslation();
  const languageLabels = useLanguageLabels();
  // Only Adopted At and Released At are sortable, as in legacy.
  const columns: Column<StudyHelperHistoryRecord>[] = [
    {
      key: 'studyHelper',
      title: t('lessons.study-helper'),
    },
    {
      key: 'location',
      title: t('wbs.location'),
    },
    {
      key: 'languages',
      // A singular header over a list, as in legacy.
      title: t('wbs.language'),
      render: (languages) =>
        languages
          .map((language) => languageLabels[language] ?? language)
          .join(', '),
    },
    {
      key: 'adoptedAt',
      title: t('wbs.adopted-at'),
      sortable: true,
      render: (value) => formatLongDate(value),
    },
    {
      key: 'releasedAt',
      title: t('wbs.released-at'),
      sortable: true,
      render: (value) => (value ? formatLongDate(value) : t('wbs.n-a-2')),
    },
    {
      key: 'releasedBy',
      title: t('wbs.released-by'),
      // Many released rows have no releaser recorded.
      render: (value) => value || t('wbs.n-a-2'),
    },
  ];

  // Unlike legacy, no search box or text filters: `WbsTable` doesn't apply them
  // in client mode. Column settings do work client-side.
  return (
    <WbsTable
      title={studentName}
      data={data}
      columns={columns}
      showSettings
      cellStyle={{ padding: '10px 8px' }}
    />
  );
}
